import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { startCheckout, openBillingPortal } from '@/lib/billing';
import {
  Settings as SettingsIcon,
  CreditCard,
  Building2,
  Users,
  Link2,
  Loader2,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { errorMessage } from '@/lib/errors';

const settingsTabs = [
  { id: 'workspace', label: 'Workspace', icon: Building2, path: '/app/settings' },
  { id: 'team', label: 'Team', icon: Users, path: '/app/settings/team' },
  { id: 'integrations', label: 'Integrations', icon: Link2, path: '/app/settings/integrations' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/app/settings/billing' },
];

export default function SettingsBilling() {
  const { workspaces, activeWorkspaceId, refetchWorkspaces, isLoading: workspacesLoading } = useWorkspaceScope();
  const activeWorkspace = activeWorkspaceId
    ? workspaces.find((w) => w.workspace_id === activeWorkspaceId)
    : null;
  const plan = activeWorkspace?.plan ?? 'base';
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!activeWorkspaceId) {
      toast({ title: 'Error', description: 'No workspace selected.', variant: 'destructive' });
      return;
    }
    setCheckoutLoading(true);
    try {
      await startCheckout('pro', activeWorkspaceId);
      toast({ title: 'Redirecting…', description: 'Taking you to checkout.' });
    } catch (e) {
      setCheckoutLoading(false);
      toast({
        title: 'Checkout failed',
        description: (
          <>
            {(e as Error)?.message ?? errorMessage(e, 'Could not start checkout. Check your connection and try again.')}
            <span className="block mt-2 text-xs opacity-90">If you just changed env vars, redeploy on Vercel.</span>
          </>
        ),
        variant: 'destructive',
      });
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      await openBillingPortal(activeWorkspaceId ?? undefined);
      toast({ title: 'Redirecting…', description: 'Opening billing portal.' });
    } catch (e) {
      setPortalLoading(false);
      if ((e as Error & { code?: string })?.code === 'NO_CUSTOMER') {
        toast({ title: 'No subscription yet', description: 'Upgrade to Pro first to manage billing and payment methods.', variant: 'destructive' });
        return;
      }
      toast({
        title: 'Portal failed',
        description: errorMessage(
          e,
          'Could not open billing portal. If you haven’t subscribed yet, upgrade first or try again later.'
        ),
        variant: 'destructive',
      });
    }
  };

  const handleRefreshStatus = async () => {
    setRefreshLoading(true);
    try {
      await refetchWorkspaces();
      toast({ title: 'Updated', description: 'Billing status refreshed.' });
    } catch (e) {
      toast({
        title: 'Refresh failed',
        description: errorMessage(e, 'Could not refresh. Try again.'),
        variant: 'destructive',
      });
    } finally {
      setRefreshLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground text-sm">Manage your workspace and billing</p>
        </div>
      </div>

      <div className="flex gap-2 mb-8 border-b">
        {settingsTabs.map((tab) => (
          <Link
            key={tab.id}
            to={tab.path}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab.path === '/app/settings/billing'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      <div className="max-w-2xl">
        <div className="bg-card border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Billing & plan</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Current plan for this workspace: <strong className="text-foreground">{plan === 'pro' ? 'Pro' : 'Base'}</strong>.
            Upgrade to Pro for more workspaces and features.
          </p>
          <div className="flex flex-wrap gap-3">
            {plan !== 'pro' && (
              <Button onClick={handleUpgrade} disabled={checkoutLoading} size="default">
                {checkoutLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Upgrade to Pro
              </Button>
            )}
            <Button variant="outline" onClick={handleManageBilling} disabled={portalLoading} size="default">
              {portalLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <ExternalLink className="w-4 h-4 mr-2" />
              Manage billing
            </Button>
            <Button
              variant="ghost"
              size="default"
              onClick={handleRefreshStatus}
              disabled={refreshLoading || workspacesLoading}
            >
              {refreshLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh status
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Manage subscription, payment methods, and invoices in the Stripe Customer Portal.
          </p>
        </div>
      </div>
    </div>
  );
}
